'use client';

import { AnimatePresence, motion } from 'motion/react';
import { useSelectedLayoutSegments } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import Button from '@/components/shared/Button';
import Icon from '@/components/shared/Icon';
import cn from '@/lib/cn';
import { CONSENT_TOOL_STORAGE_KEYS } from '@/lib/constants';
import sendGTMEvent from '@/lib/send-gtm-event';

type CookieType = {
  id: string;
  required?: boolean;
  name: string;
  description: string;
  services?: {
    name: string;
    provider: string;
    description: string;
    privacyPolicyUrl: string;
  }[];
  onAccept?: () => void;
  onReject?: () => void;
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
function gtag() {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

function injectGoogleTagManager() {
  if (typeof window === 'undefined' || document.querySelector('#gtm-script')) {
    return;
  }

  sendGTMEvent({
    event: 'gtm.js',
    'gtm.start': new Date().getTime(),
  });

  const script = document.createElement('script');
  script.id = 'gtm-script';
  script.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-WPB832';
  script.async = true;

  const firstScript = document.getElementsByTagName('script')[0];

  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

function removeGoogleTagManager() {
  if (!document.querySelector('#gtm-script')) return;

  const consentsWithGtmDependency = [
    `${CONSENT_TOOL_STORAGE_KEYS.cookieChoicePrefix}marketing`,
    `${CONSENT_TOOL_STORAGE_KEYS.cookieChoicePrefix}analytics`,
  ].map((key) => localStorage.getItem(key) === 'true');

  if (consentsWithGtmDependency.some((value) => value)) return;

  // Reload the page to remove GTM script completely
  window.location.reload();
}

function onAcceptMarketing() {
  if (typeof window === 'undefined') return;

  if (typeof window.dataLayer === 'undefined') {
    setTimeout(onAcceptMarketing, 100);
    return;
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  gtag('consent', 'update', {
    ad_personalization: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
  });

  sendGTMEvent({ blockGoogleAds: false });
}

function onRejectMarketing() {
  if (typeof window === 'undefined') return;

  if (typeof window.dataLayer === 'undefined') {
    setTimeout(onRejectMarketing, 100);
    return;
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  gtag('consent', 'update', {
    ad_personalization: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
  });

  sendGTMEvent({ blockGoogleAds: true });

  removeGoogleTagManager();
}

function onAcceptAnalytics({
  locale,
  segments,
}: {
  locale: string;
  segments: string[];
}) {
  if (typeof window === 'undefined') return;

  if (typeof window.dataLayer === 'undefined') {
    setTimeout(() => onAcceptAnalytics({ locale, segments }), 100);
    return;
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  gtag('consent', 'update', {
    analytics_storage: 'granted',
  });

  sendGTMEvent({ blockGoogleAnalytics: false });

  const pageId =
    segments[0] === 'press' && segments.length > 1
      ? 'about.press.article'
      : `about.${segments[0]}`;

  sendGTMEvent({
    event: 'page_view',
    event_name: 'page',
    page: {
      languageSettings: locale,
      pageId,
      pageType: pageId,
    },
  });
}

function onRejectAnalytics() {
  if (typeof window === 'undefined') return;

  if (typeof window.dataLayer === 'undefined') {
    setTimeout(onRejectAnalytics, 100);
    return;
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  gtag('consent', 'update', {
    analytics_storage: 'denied',
  });

  sendGTMEvent({ blockGoogleAnalytics: true });

  removeGoogleTagManager();
}

function setCookieChoicesMade() {
  localStorage.setItem(CONSENT_TOOL_STORAGE_KEYS.configured, '1');
}

function runRequiredCookieCallbacks(cookieTypes: CookieType[]) {
  cookieTypes.forEach((type) => {
    if (type.required && typeof type.onAccept === 'function') {
      type.onAccept();
    }
  });
}

function runAcceptedCookieCallbacks(cookieTypes: CookieType[]) {
  const acceptedCookies = cookieTypes.reduce<Record<string, boolean>>(
    (acc, type) => {
      acc[type.id] =
        localStorage.getItem(
          `${CONSENT_TOOL_STORAGE_KEYS.cookieChoicePrefix}${type.id}`,
        ) === 'true';
      return acc;
    },
    {},
  );

  cookieTypes.forEach((type) => {
    if (type.required) return;
    if (acceptedCookies[type.id] && typeof type.onAccept === 'function') {
      type.onAccept();
    }
  });
}

function storeWithoutChoice(cookieTypes: CookieType[]) {
  cookieTypes.forEach((type) => {
    if (type.required === true) {
      localStorage.setItem(
        `${CONSENT_TOOL_STORAGE_KEYS.cookieChoicePrefix}${type.id}`,
        'true',
      );

      if (typeof type.onAccept === 'function') type.onAccept();
    } else {
      localStorage.setItem(
        `${CONSENT_TOOL_STORAGE_KEYS.cookieChoicePrefix}${type.id}`,
        'false',
      );

      if (typeof type.onReject === 'function') type.onReject();
    }
  });
}

function storeGlobalChoice(cookieTypes: CookieType[], allAccepted: boolean) {
  cookieTypes.forEach((type) => {
    if (type.required === true) {
      localStorage.setItem(
        `${CONSENT_TOOL_STORAGE_KEYS.cookieChoicePrefix}${type.id}`,
        'true',
      );

      if (typeof type.onAccept === 'function') type.onAccept();
    } else {
      localStorage.setItem(
        `${CONSENT_TOOL_STORAGE_KEYS.cookieChoicePrefix}${type.id}`,
        allAccepted.toString(),
      );

      if (allAccepted) {
        if (typeof type.onAccept === 'function') type.onAccept();
      } else {
        if (typeof type.onReject === 'function') type.onReject();
      }
    }
  });
}

function Banner({
  onChoice,
  onOpenPreferences,
}: {
  onChoice: (value: boolean) => void;
  onOpenPreferences: () => void;
}) {
  const t = useTranslations('consent-tool');

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-x-5 bottom-5 z-[9999] max-w-lg rounded-lg bg-white p-5 sm:right-8 sm:bottom-8 sm:left-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
      >
        <p className="text-xs text-black/70">{t('banner.text')}</p>

        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <Button
            kind="secondary"
            size="sm"
            showIcon={false}
            onClick={() => onChoice(true)}
          >
            {t('accept-all-button')}
          </Button>
          <Button
            kind="secondary"
            size="sm"
            showIcon={false}
            onClick={() => onChoice(false)}
          >
            {t('reject-non-essential-button')}
          </Button>
          <button
            className="rounded-full px-2 text-xs font-medium transition-colors hover:text-black/70"
            type="button"
            onClick={onOpenPreferences}
          >
            {t('banner.preferences-button')}
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function CookieType({
  description,
  id,
  name,
  onAccept,
  onReject,
  required,
  services,
}: CookieType) {
  const t = useTranslations('consent-tool');
  const [isChecked, setIsChecked] = useState(
    localStorage.getItem(
      `${CONSENT_TOOL_STORAGE_KEYS.cookieChoicePrefix}${id}`,
    ) === 'true' || !!required,
  );
  const [showDetails, setShowDetails] = useState(false);

  function onChange(event: React.ChangeEvent<HTMLInputElement>) {
    const isChecked = event.target.checked;

    localStorage.setItem(
      `${CONSENT_TOOL_STORAGE_KEYS.cookieChoicePrefix}${id}`,
      isChecked.toString(),
    );
    setIsChecked(isChecked);

    if (isChecked && typeof onAccept === 'function') {
      onAccept();
    } else if (!isChecked && typeof onReject === 'function') {
      onReject();
    }
  }

  return (
    <fieldset>
      <legend className="font-medium">{name}</legend>

      <div className="mt-1 flex justify-between gap-8">
        <div className="text-xs">
          <p className="inline text-black/70">{description}</p>{' '}
          {services && (
            <button
              className="underline hover:no-underline"
              type="button"
              onClick={() => setShowDetails((prev) => !prev)}
            >
              {showDetails ? t('details.hide') : t('details.show')}
            </button>
          )}
        </div>

        <label
          className={cn(
            'relative h-6 w-10 shrink-0 rounded-full transition-colors',
            {
              'bg-green-30': !required && isChecked,
              'bg-neutral-50': required,
              'bg-red-40': !required && !isChecked,
            },
          )}
          htmlFor={`cookie-${id}`}
        >
          <span
            className={cn(
              'absolute top-0.5 left-0.5 size-5 rounded-full bg-white transition-transform',
              {
                'translate-x-4': isChecked,
              },
            )}
          />

          <input
            id={`cookie-${id}`}
            className="absolute inset-0 appearance-none"
            type="checkbox"
            checked={isChecked}
            disabled={required}
            onChange={onChange}
          />
        </label>
      </div>

      {services && showDetails && (
        <ul>
          {services.map((service) => (
            <li
              key={service.name}
              className="mt-2 grid gap-3 rounded-md border border-black/10 p-2.5 text-xs text-black/70 sm:grid-cols-[auto_1fr]"
            >
              <p className="font-medium">{t('details.name')}</p>
              <p className="font-medium">{service.name}</p>

              <p className="font-medium">{t('details.provider')}</p>
              <p>{service.provider}</p>

              <p className="font-medium">{t('details.description')}</p>
              <p>{service.description}</p>

              <p className="font-medium">{t('details.privacy-policy')}</p>
              <p>
                <a
                  href={service.privacyPolicyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:no-underline"
                >
                  {service.privacyPolicyUrl}
                </a>
              </p>
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}

function Preferences({
  cookieTypes,
  onClose,
  onGlobalChoice,
}: {
  cookieTypes: CookieType[];
  onClose: () => void;
  onGlobalChoice: (value: boolean) => void;
}) {
  const t = useTranslations();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    const focusableEls = ref.current.querySelectorAll<HTMLElement>(
      'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstEl) {
            lastEl.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastEl) {
            firstEl.focus();
            e.preventDefault();
          }
        }
      }

      if (e.key === 'Escape') {
        onClose();
      }
    }

    ref.current.addEventListener('keydown', handleKeyDown);
    firstEl?.focus();
  }, [onClose]);

  return (
    <AnimatePresence>
      <motion.div
        ref={ref}
        className="fixed top-1/2 left-1/2 z-[10000] flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2.5rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg bg-white"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
      >
        <header className="flex items-center justify-between gap-5 border-b border-black/10 p-5">
          <h2 className="font-display text-display-2xs font-medium uppercase">
            {t('consent-tool.preferences.title')}
          </h2>

          <button
            className="hover:text-neutral-10 flex size-8 shrink-0 items-center justify-center rounded-md bg-black/5 transition-colors hover:bg-black/10"
            type="button"
            aria-label={t('close')}
            onClick={onClose}
          >
            <Icon
              name="times"
              className="size-3"
            />
          </button>
        </header>

        <div className="space-y-5 overflow-auto p-5 text-sm">
          <p className="text-black/70">{t('consent-tool.preferences.text')}</p>

          {cookieTypes.map((type) => (
            <CookieType
              key={type.id}
              {...type}
            />
          ))}
        </div>

        <footer className="flex flex-col justify-end gap-2 border-t border-black/5 p-5 sm:flex-row">
          <Button
            kind="secondary"
            size="sm"
            showIcon={false}
            onClick={() => onGlobalChoice(true)}
          >
            {t('consent-tool.accept-all-button')}
          </Button>
          <Button
            kind="secondary"
            size="sm"
            showIcon={false}
            onClick={() => onGlobalChoice(false)}
          >
            {t('consent-tool.reject-non-essential-button')}
          </Button>
        </footer>
      </motion.div>
    </AnimatePresence>
  );
}

export function Trigger({
  children,
  className = undefined,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      className={className}
      type="button"
      onClick={() => window.dispatchEvent(new CustomEvent('open-consent-tool'))}
    >
      {children}
    </button>
  );
}

export default function ConsentTool() {
  const locale = useLocale();
  const segments = useSelectedLayoutSegments();
  const t = useTranslations('consent-tool');

  const cookieTypes: CookieType[] = useMemo(
    () => [
      {
        description: t('types.essential.description'),
        id: 'essential',
        name: t('types.essential.name'),
        required: true,
      },
      {
        description: t('types.marketing.description'),
        id: 'marketing',
        name: t('types.marketing.name'),
        onAccept: () => {
          injectGoogleTagManager();
          onAcceptMarketing();
        },
        onReject: onRejectMarketing,
        services: [
          {
            description: t('service-descriptions.google-tag-manager'),
            name: 'Google Tag Manager',
            privacyPolicyUrl: 'https://policies.google.com/privacy',
            provider: 'Google (Gordon House, Barrow Street, Dublin 4, Ireland)',
          },
          {
            description: t('service-descriptions.google-ads'),
            name: 'Google Ads',
            privacyPolicyUrl: 'https://policies.google.com/privacy',
            provider: 'Google (Gordon House, Barrow Street, Dublin 4, Ireland)',
          },
        ],
      },
      {
        description: t('types.analytics.description'),
        id: 'analytics',
        name: t('types.analytics.name'),
        onAccept: () => {
          injectGoogleTagManager();
          onAcceptAnalytics({ locale, segments });
        },
        onReject: onRejectAnalytics,
        services: [
          {
            description: t('service-descriptions.google-tag-manager'),
            name: 'Google Tag Manager',
            privacyPolicyUrl: 'https://policies.google.com/privacy',
            provider: 'Google (Gordon House, Barrow Street, Dublin 4, Ireland)',
          },
          {
            description: t('service-descriptions.google-analytics'),
            name: 'Google Analytics',
            privacyPolicyUrl: 'https://policies.google.com/privacy',
            provider: 'Google (Gordon House, Barrow Street, Dublin 4, Ireland)',
          },
        ],
      },
    ],
    [locale, segments, t],
  );

  const [showBackdrop, setShowBackdrop] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  function handleOpenPreferences() {
    setShowBackdrop(true);
    setShowBanner(false);
    setShowPreferences(true);
  }

  function handleClosePreferences() {
    if (!localStorage.getItem(CONSENT_TOOL_STORAGE_KEYS.configured)) {
      storeWithoutChoice(cookieTypes);
    }

    setCookieChoicesMade();
    setShowPreferences(false);
    setShowBackdrop(false);
  }

  function handleGlobalChoice(allAccepted: boolean) {
    setCookieChoicesMade();
    setShowBanner(false);
    setShowBackdrop(false);
    setShowPreferences(false);

    storeGlobalChoice(cookieTypes, allAccepted);
  }

  useEffect(() => {
    if (localStorage.getItem(CONSENT_TOOL_STORAGE_KEYS.configured)) {
      runRequiredCookieCallbacks(cookieTypes);
      runAcceptedCookieCallbacks(cookieTypes);
    } else {
      setShowBackdrop(true);
      setShowBanner(true);
    }

    window.addEventListener('open-consent-tool', handleOpenPreferences);

    return () => {
      window.removeEventListener('open-consent-tool', handleOpenPreferences);
    };
  }, [cookieTypes]);

  return (
    <>
      {showBanner && (
        <Banner
          onChoice={handleGlobalChoice}
          onOpenPreferences={handleOpenPreferences}
        />
      )}

      {showPreferences && (
        <Preferences
          cookieTypes={cookieTypes}
          onGlobalChoice={handleGlobalChoice}
          onClose={handleClosePreferences}
        />
      )}

      <AnimatePresence>
        {showBackdrop && (
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/15"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>
    </>
  );
}
